import React, { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { cn } from '../lib/utils';
import { filterOptions, DEFAULT_MAX_VISIBLE } from '../lib/filterOptions';

/**
 * A dropdown you can type into.
 *
 * The city master runs to several hundred entries, which is more than a plain
 * <Select> can reasonably be scrolled through on a phone. Options are filtered as
 * the user types and the list is windowed, so the size of the master does not
 * decide how much gets rendered.
 *
 * Deliberately not built on cmdk: cmdk owns keyboard handling and does its own
 * fuzzy scoring, which fights the plain "starts with, then contains" order field
 * staff expect when hunting for a place name.
 */
const MAX_VISIBLE = DEFAULT_MAX_VISIBLE;

export const SearchableSelect = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  emptyMessage = 'Nothing matches that',
  disabled = false,
  testId,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const { shown, total } = useMemo(
    () => filterOptions(options, query, MAX_VISIBLE),
    [options, query]
  );

  const selected = (options || []).find((o) => o.value === value);

  const pick = (next) => {
    onChange(next);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'w-full h-12 rounded-xl justify-between font-normal',
            !selected && 'text-slate-500'
          )}
          data-testid={testId}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 w-[--radix-popover-trigger-width] min-w-[16rem]"
        align="start"
      >
        <div className="flex items-center border-b border-slate-100 px-3">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="flex h-11 w-full bg-transparent px-2 text-sm outline-none placeholder:text-slate-400"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>

        <div className="max-h-64 overflow-y-auto py-1">
          {shown.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-slate-500">{emptyMessage}</p>
          ) : (
            shown.map((o) => (
              <button
                key={o.value}
                type="button"
                onClick={() => pick(o.value)}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm',
                  'hover:bg-slate-50 active:bg-slate-100',
                  o.value === value && 'bg-sky-50 text-sky-900'
                )}
              >
                <Check
                  className={cn(
                    'h-4 w-4 shrink-0',
                    o.value === value ? 'opacity-100 text-sky-600' : 'opacity-0'
                  )}
                />
                <span className="truncate">{o.label}</span>
              </button>
            ))
          )}

          {total > shown.length && (
            <p className="px-3 py-2 text-center text-xs text-slate-400">
              Showing {shown.length} of {total} — keep typing to narrow it down
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchableSelect;
